import { useEffect, useState } from "react";
import { api, STATUS_STYLES, STATUS_LABELS, fmtTime } from "@/lib/api";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Link } from "react-router-dom";

const Kpi = ({ label, value, testid, accent }) => (
  <div className="bg-white border border-[#1A1E24]/10 rounded-lg p-6" data-testid={testid}>
    <div className="text-[10px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E]">{label}</div>
    <div
      className="mt-3 font-mono-data font-bold text-3xl tracking-tight"
      style={{ color: accent || "#1A1E24", fontVariantNumeric: "tabular-nums" }}
    >
      {value ?? "—"}
    </div>
  </div>
);

const REFRESH_MS = 30000;

export default function DashboardPage() {
  const [m, setM] = useState(null);
  const [stats, setStats] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const [metricsRes, incidentsRes, statsRes] = await Promise.all([
          api.get("/metrics"),
          api.get("/incidents"),
          api.get("/contacts/stats"),
        ]);
        if (!active) return;
        setM(metricsRes.data);
        setStats(statsRes.data);
        setIncidents(incidentsRes.data.slice(0, 8));
        setLastUpdated(new Date());
      } catch (e) {
        console.error("Dashboard refresh failed (keeping last good data)", e);
      }
    };
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  return (
    <div className="px-10 py-10 max-w-[1400px]" data-testid="dashboard-page">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-[0.18em] text-[#5B6B7E]">
          <span className="w-6 h-px bg-[#E01A2E]" />
          OPERATIONS
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E]" data-testid="dashboard-live-indicator">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-[#1E9E63] opacity-75 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#1E9E63]" />
          </span>
          LIVE
          {lastUpdated && (
            <span className="text-[#a8b3c1] normal-case tracking-normal ml-1" data-testid="dashboard-last-updated">
              · updated {lastUpdated.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
            </span>
          )}
        </div>
      </div>
      <h1 className="font-heading text-5xl leading-[1.05] tracking-tight text-[#1A1E24]" style={{ fontWeight: 500 }}>
        Live dispatch,{" "}
        <em className="italic text-[#5B6B7E]" style={{ fontWeight: 400 }}>
          at a glance
        </em>
        .
      </h1>
      <p className="text-[#5B6B7E] text-base mt-3 max-w-2xl">
        Performance metrics across every dispatch — incidents, live activity, and how the member base is growing.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-10">
        <Kpi label="Total Incidents" value={m?.total} testid="kpi-total" />
        <Kpi label="Open" value={m?.open} testid="kpi-open" accent="#F5A623" />
        <Kpi label="Closed" value={m?.closed} testid="kpi-closed" accent="#1E9E63" />
        <Kpi label="Live now" value={m?.live_active ?? incidents.filter(i => i.status !== "closed").length} testid="kpi-live" />
        <Kpi label={`Closed in ${m?.month_label?.split(" ")[0] || "this month"}`} value={m?.closed_this_month} testid="kpi-closed-month" />
        <Kpi label="Members" value={stats?.total} testid="kpi-members" />
        <Kpi label="App activated" value={stats?.activated} testid="kpi-activated" accent="#1E9E63" />
        <Kpi label="Debit orders signed" value={stats?.do_signed} testid="kpi-do-signed" accent="#0B1E3B" />
      </div>

      <div className="grid lg:grid-cols-5 gap-6 mt-10">
        <div className="lg:col-span-3 bg-white border border-[#1A1E24]/10 rounded-lg p-7">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-heading text-2xl tracking-tight" style={{ fontWeight: 500 }}>
              Incidents per client
            </h2>
            <span className="text-[10px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E]">
              Rolling 30 days
            </span>
          </div>
          {m?.by_client?.length ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={m.by_client} margin={{ top: 8, right: 8, left: -8, bottom: 4 }}>
                <CartesianGrid stroke="rgba(26,30,36,0.06)" vertical={false} />
                <XAxis dataKey="client" stroke="#7a8ba0" fontSize={11} tickLine={false} axisLine={{ stroke: "rgba(26,30,36,0.1)" }} />
                <YAxis stroke="#7a8ba0" fontSize={11} allowDecimals={false} tickLine={false} axisLine={false} />
                <Tooltip
                  contentStyle={{ background: "#fff", border: "1px solid rgba(26,30,36,0.12)", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#1A1E24", fontWeight: 600 }}
                  cursor={{ fill: "rgba(224,26,46,0.05)" }}
                />
                <Bar dataKey="count" fill="#E01A2E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="text-[#a8b3c1] text-sm py-16 text-center font-mono-data tracking-widest">
              NO INCIDENT DATA YET
            </div>
          )}
        </div>

        <div className="lg:col-span-2 bg-white border border-[#1A1E24]/10 rounded-lg p-7">
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-heading text-2xl tracking-tight" style={{ fontWeight: 500 }}>
              Recent incidents
            </h2>
            <Link
              to="/incidents"
              className="text-[11px] font-mono-data uppercase tracking-[0.16em] text-[#E01A2E] hover:underline"
              data-testid="dashboard-view-all"
            >
              View all →
            </Link>
          </div>
          {incidents.length === 0 && (
            <div className="text-[#a8b3c1] text-sm py-16 text-center font-mono-data tracking-widest">
              NO INCIDENTS — SIMULATE ONE FROM EMAIL INTAKE
            </div>
          )}
          {incidents.map((i) => (
            <div
              key={i.id}
              className="flex items-center justify-between py-3 border-b border-[#1A1E24]/8 last:border-0"
              data-testid={`recent-incident-${i.ref}`}
            >
              <div className="min-w-0">
                <div className="font-mono-data text-sm text-[#1A1E24] truncate">{i.ref}</div>
                <div className="text-[#5B6B7E] text-xs mt-0.5 truncate">
                  {i.client_name || "Unidentified"}
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0 ml-3">
                <span className="font-mono-data text-[10px] text-[#a8b3c1]">{fmtTime(i.received_at)}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 border rounded-sm ${STATUS_STYLES[i.status]}`}>
                  {STATUS_LABELS[i.status]}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <ClosedByReason m={m} />
    </div>
  );
}

function ClosedByReason({ m }) {
  const rows = m?.closed_by_reason || [];
  const total = m?.closed_this_month || 0;
  const monthLabel = m?.month_label || "This month";
  const REASON_ACCENTS = {
    "False alarm": "#F5A623",
    "Test incident": "#5B6B7E",
    "Duplicate": "#7a8ba0",
    "Other": "#0B1E3B",
    "Manual close": "#5B6B7E",
  };
  const accentFor = (row) =>
    row.category === "manual" ? (REASON_ACCENTS[row.reason] || "#F5A623") : "#1E9E63";

  return (
    <div className="mt-10 bg-white border border-[#1A1E24]/10 rounded-lg p-7" data-testid="closed-by-reason">
      <div className="flex items-baseline justify-between flex-wrap gap-3 mb-6">
        <div>
          <h2 className="font-heading text-2xl tracking-tight" style={{ fontWeight: 500 }}>
            Closed <em style={{ fontStyle: "italic", color: "#5B6B7E", fontWeight: 400 }}>by reason</em>
          </h2>
          <div className="text-[11px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E] mt-1">
            {monthLabel} · {total} closed
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono-data uppercase tracking-[0.16em] text-[#5B6B7E]">
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 2, background: "#F5A623" }} /> Manual</span>
          <span className="flex items-center gap-1.5"><span style={{ width: 8, height: 8, borderRadius: 2, background: "#1E9E63" }} /> Resolved</span>
        </div>
      </div>

      {rows.length === 0 ? (
        <div className="text-[#a8b3c1] text-sm py-10 text-center font-mono-data tracking-widest" data-testid="closed-by-reason-empty">
          NO CLOSURES THIS MONTH YET
        </div>
      ) : (
        <div className="grid gap-3">
          {rows.map((r) => {
            const pct = total ? Math.round((r.count / total) * 100) : 0;
            const accent = accentFor(r);
            return (
              <div key={`${r.category}-${r.reason}`} data-testid={`closed-row-${r.reason.replace(/\s+/g, "-").toLowerCase()}`} className="flex items-center gap-4">
                <div className="w-40 shrink-0">
                  <div className="text-sm text-[#1A1E24]" style={{ letterSpacing: "-0.005em" }}>{r.reason}</div>
                  <div className="text-[10px] font-mono-data uppercase tracking-[0.14em] text-[#a8b3c1] mt-0.5">
                    {r.category === "manual" ? "MANUAL CLOSE" : "CSG COMPLETED"}
                  </div>
                </div>
                <div className="flex-1 h-2 rounded-full overflow-hidden" style={{ background: "#EFECE4" }}>
                  <div style={{ width: `${pct}%`, height: "100%", background: accent, transition: "width 0.6s ease" }} />
                </div>
                <div className="w-24 text-right shrink-0">
                  <span className="font-mono-data text-base text-[#1A1E24]" style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{r.count}</span>
                  <span className="font-mono-data text-[11px] text-[#a8b3c1] ml-2" style={{ fontVariantNumeric: "tabular-nums" }}>{pct}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
